import React from 'react';
import { Link } from 'react-router-dom';
import ResearchCard from '../components/ResearchCard';
import { RESEARCH_AREAS } from '../data/research';

export default function Research() {
  return (
    <>
      {/* Hero */}
      <section className="px-6 pt-12 pb-16 md:pt-20 md:pb-24 background-grid">
        <div className="max-w-4xl mx-auto">
          <span className="text-xs uppercase tracking-[0.3em] text-gray-500">Research</span>
          <h1 className="text-4xl md:text-6xl font-light mt-4 mb-6 leading-tight">
            Foundational research, applied to mental health.
          </h1>
          <div className="w-24 h-px bg-gray-900 mb-8" />
          <p className="text-lg text-gray-700 leading-relaxed max-w-3xl">
            Cognitics conducts foundational research at the intersection of artificial
            intelligence, cognitive science, and psychology — and translates it into
            systems that work in the real world.
          </p>
        </div>
      </section>

      {/* Focus areas */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <span className="text-xs uppercase tracking-[0.3em] text-gray-500">What We Study</span>
            <h2 className="text-4xl md:text-5xl font-light mt-4 mb-6">Focus areas</h2>
            <div className="w-24 h-px bg-gray-900" />
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {RESEARCH_AREAS.map((r, i) => (
              <ResearchCard key={r.title} title={r.title} description={r.description} delay={i * 100} />
            ))}
          </div>
        </div>
      </section>

      {/* From research to impact */}
      <section className="py-24 md:py-32 px-6" style={{ backgroundColor: '#f5f6ed' }}>
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <span className="text-xs uppercase tracking-[0.3em] text-gray-500">Real-World Application</span>
            <h2 className="text-4xl md:text-5xl font-light mt-4 mb-6">From research to impact</h2>
            <div className="w-24 h-px bg-gray-900" />
          </div>

          <div className="max-w-4xl">
            <p className="text-xl text-gray-700 leading-relaxed mb-12">
              Cognitics follows a research-to-systems approach. Research outputs are
              translated into applied AI platforms designed as two-sided systems —
              this is the work that becomes Bloora.
            </p>

            <div className="space-y-8">
              <div className="border-l-4 border-gray-300 pl-8 hover:border-gray-900 transition-colors duration-300">
                <h3 className="text-xl font-medium mb-3">Professional-facing systems</h3>
                <p className="text-gray-700 leading-relaxed">
                  Analyze patient data, identify correlations, and provide structured,
                  model-driven insights to support clinical understanding and decision-making.
                </p>
              </div>

              <div className="border-l-4 border-gray-300 pl-8 hover:border-gray-900 transition-colors duration-300">
                <h3 className="text-xl font-medium mb-3">User-facing systems</h3>
                <p className="text-gray-700 leading-relaxed">
                  Act as intelligent companions, enabling individuals to log data,
                  reflect on patterns, remain anonymous when needed, and gain
                  data-driven insight into their own mental states.
                </p>
              </div>
            </div>

            <div className="mt-12">
              <Link to="/bloora" className="btn-ghost">See it in product → Bloora</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Collaborations */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <span className="text-xs uppercase tracking-[0.3em] text-gray-500">Work With Us</span>
          <h2 className="text-4xl md:text-5xl font-light mt-4 mb-6">Collaborations & open positions</h2>
          <div className="w-20 h-px bg-gray-900 mb-10" />
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            We actively seek partnerships with universities, research institutions,
            and clinical organizations. We're also hiring AI/ML researchers, data
            scientists, and people with backgrounds in cognitive science or psychology
            who want to apply that rigor to mental health.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact" className="btn-ghost">Get in touch</Link>
            <a href="mailto:careers@cognitics.org" className="btn-ghost">View opportunities</a>
          </div>
        </div>
      </section>
    </>
  );
}
